import { useSyncExternalStore } from 'react';

/**
 * App-wide status shown in the header banner: connection, the time of the offline
 * copy and whether a new version is waiting. A tiny external store (no React context),
 * so plain modules such as the API client can update it.
 */
export interface AppStatus {
  online: boolean;
  /** When the server built the offline copy in use (ISO), if there is one. */
  offlineCopyAt: string | null;
  updateAvailable: boolean;
}

let state: AppStatus = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  offlineCopyAt: null,
  updateAvailable: false,
};
const listeners = new Set<() => void>();
const reconnectListeners = new Set<() => void>();
let probeTimer: ReturnType<typeof setInterval> | undefined;

function set(patch: Partial<AppStatus>) {
  const next = { ...state, ...patch };
  if (Object.keys(patch).every((k) => next[k as keyof AppStatus] === state[k as keyof AppStatus])) return;
  state = next;
  listeners.forEach((l) => l());
}

export function getAppStatus(): AppStatus {
  return state;
}

export function setOfflineCopyAt(at: string | null): void {
  set({ offlineCopyAt: at });
}

export function setUpdateAvailable(available: boolean): void {
  set({ updateAvailable: available });
}

/** Called after a request could not reach the server. */
export function markOffline(): void {
  if (!state.online) return;
  set({ online: false });
  // Check now and then whether the server is reachable again.
  clearInterval(probeTimer);
  probeTimer = setInterval(() => void probe(), 15_000);
}

/** Called after any answer from the server. */
export function markOnline(): void {
  if (state.online) return;
  clearInterval(probeTimer);
  set({ online: true });
  reconnectListeners.forEach((l) => l());
}

async function probe(): Promise<void> {
  try {
    const res = await fetch('/api/health', { cache: 'no-store' });
    if (res.ok) markOnline();
  } catch {
    // Still offline.
  }
}

/** Runs after the connection comes back (refresh data, resync the offline copy). */
export function onReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', markOffline);
  window.addEventListener('online', () => void probe());
}

export function useAppStatus(): AppStatus {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

export const useOnline = () => useAppStatus().online;
